import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { NAV, PHONE, PHONE_HREF, LOGO } from "../lib/content";
import { scrollToId } from "../hooks/useSmoothScroll";

export const Navbar = ({ onConsult }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-amber-500/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between">
        <button
          onClick={() => go("home")}
          data-testid="nav-logo"
          className="flex items-center gap-3 shrink-0"
        >
          <img src={LOGO} alt="B. Thornton Capital & Asset Group" className="h-11 sm:h-14 w-auto" />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              data-testid={`nav-${item.id}`}
              className="text-[13px] font-sans tracking-[0.14em] uppercase text-zinc-400 hover:text-amber-400 transition-colors duration-300"
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href={PHONE_HREF}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm text-zinc-300 hover:text-amber-400 transition-colors"
          >
            <Phone className="h-4 w-4 text-amber-500" />
            {PHONE}
          </a>
          <button
            onClick={onConsult}
            data-testid="nav-consult-btn"
            className="bg-gold-foil text-black text-[12px] font-bold tracking-[0.12em] uppercase px-6 py-3 hover:brightness-110 transition-all duration-300"
          >
            Free 15-Min Consultation
          </button>
        </div>

        <button
          className="lg:hidden text-amber-400"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-amber-500/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  onClick={() => go(item.id)}
                  data-testid={`nav-mobile-${item.id}`}
                  className="text-left text-base font-serif text-zinc-200 hover:text-amber-400"
                >
                  {item.label}
                </button>
              ))}
              <a href={PHONE_HREF} className="flex items-center gap-2 text-amber-400 pt-2">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <button
                onClick={() => go("contact")}
                className="mt-2 bg-gold-foil text-black font-bold uppercase tracking-wide py-3"
              >
                Free 15-Min Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
